import { useMutation, useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@hrms/shared';
import type {
  CreateLocationPingDto,
  LocationPingDto,
  LocationReportingStateDto,
  TrailPointDto,
} from '@hrms/shared';
import { get, post } from '@/lib/api-client';
import { useAuth } from '@/features/auth/use-auth';

export const locationKeys = {
  all: ['location'] as const,
  state: ['location', 'state'] as const,
  trail: (employeeId: string) => ['location', 'trail', employeeId] as const,
};

/**
 * Whether this device should report, and how often.
 *
 * Gated on the share permission the endpoint requires. Asked once a minute
 * rather than once a session so switching the feature off in Settings stops
 * devices reporting without waiting for everyone to reload.
 */
export function useReportingState() {
  const { can } = useAuth();

  return useQuery({
    queryKey: locationKeys.state,
    queryFn: () => get<LocationReportingStateDto>('/location/state'),
    enabled: can(PERMISSIONS.LOCATION_SHARE),
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useSubmitPing() {
  return useMutation({
    mutationFn: (body: CreateLocationPingDto) => post<LocationPingDto>('/location/pings', body),
    // Deliberately silent. A failed position is not something the person being
    // located can act on, and a toast every few minutes would be punishment for
    // standing somewhere with poor signal.
    retry: false,
  });
}

/** One person's route today, as coordinates the map projects itself. */
export function useLocationTrail(employeeId: string | null) {
  const { canAny } = useAuth();

  return useQuery({
    queryKey: locationKeys.trail(employeeId ?? ''),
    queryFn: () => get<TrailPointDto[]>(`/location/map/trail/${employeeId as string}`),
    enabled:
      Boolean(employeeId) && canAny(PERMISSIONS.LOCATION_READ_TEAM, PERMISSIONS.LOCATION_READ_ALL),
  });
}
